import { useFormContext } from 'react-hook-form'
import { QueryFunction, useQuery, UseQueryResult } from 'react-query'
import { useLocation, useSearchParams } from 'react-router-dom'
import { AxiosError, AxiosResponse } from 'axios'
import { has } from 'lodash'

import { HEADERS_NAMES_SLUG } from 'common/constant'
import {
  AxleExchangeResponse,
  AxleIgnitionResponse,
  Element,
  FlowSessionState,
  FlowState,
  GtmContainer,
  LocationState,
  Page,
  SearchableField,
} from 'common/interfaces'
import { useHeapDispatch } from 'context/Heap'
import { useSessionDispatch, useSessionState } from 'context/Session'
import api from 'utils/api'
import { adjustersFlowName } from 'views/AdjustersHub/constants'

import { useLocalStorage } from './hook'

export function useFlowSlug(): string {
  const { pathname } = useLocation()
  let flowSlug = pathname.slice(1)
  const flowSlugFromHeader = sessionStorage.getItem(HEADERS_NAMES_SLUG.flow)

  if (flowSlugFromHeader !== '' && flowSlugFromHeader != null) {
    flowSlug = flowSlugFromHeader
  }

  return flowSlug
}

export function usePrevFlowSlug(): string {
  const location = useLocation()
  const prevFlowSlug = (location.state as LocationState)?.from
  return prevFlowSlug ?? ''
}

export function getCompanySlug(): string {
  const { host } = window.location
  let [companySlug] = host.split('.')
  const companySlugFromHeader = sessionStorage.getItem(HEADERS_NAMES_SLUG.company)

  if (companySlugFromHeader !== '' && companySlugFromHeader != null) {
    companySlug = companySlugFromHeader
  }

  return companySlug
}

const getFlow: QueryFunction<FlowState> = async ({ queryKey }) => {
  const [, company, flow] = queryKey

  const queryParams = new URLSearchParams(location.search)
  const SIDParam = queryParams.get('s_id')
  const token = queryParams.get('token') // this is the authentication token for the flow if the flow requires authentication
  const flowSlug = window.location.pathname.replace('/', '')
  const sessionFlowId = localStorage.getItem(`${flowSlug}/sessionFlowId`)

  const response = await api.get<FlowState>('flows/by_slugs/', {
    params: {
      slug: flow,
      company__slug: company,
      token: token,
      ...((SIDParam !== null || sessionFlowId !== null) && {
        flow_session: SIDParam ?? JSON.parse(sessionFlowId!),
      }),
    },
  })
  return response.data
}

const getExtraGtmContainers: QueryFunction<GtmContainer> = async ({ queryKey }) => {
  const [, company, flow] = queryKey

  const response = await api.get<GtmContainer>('flows/extra_gtm_containers/', {
    params: {
      slug: flow,
      company_slug: company,
    },
  })

  return response.data
}

export const axleIgnition = async (
  flowSlug: string,
  companySlug: string,
  axleFieldName: string,
  sessionId: string | null
): Promise<AxleIgnitionResponse> => {
  console.log('slug', flowSlug, companySlug)
  const response: AxiosResponse<AxleIgnitionResponse> = await api.get<AxleIgnitionResponse>(
    'flows/axle_ignition/',
    {
      params: {
        slug: flowSlug,
        company__slug: companySlug,
        axle_field_name: axleFieldName,
        session_id: sessionId,
      },
    }
  )

  console.log('response', response)

  return response.data
}

export const axleExchange = async (
  flowSlug: string,
  companySlug: string,
  axleFieldName: string,
  authCode: string
): Promise<AxleExchangeResponse> => {
  const response: AxiosResponse<AxleExchangeResponse> = await api.post<AxleExchangeResponse>(
    'flows/axle_exchange_token/',
    {
      slug: flowSlug,
      company__slug: companySlug,
      axle_field_name: axleFieldName,
      authCode: authCode,
    }
  )

  return response.data
}

const fetchSearchableFields = async (
  flowSlug: string | null,
  companySlug: string
): Promise<SearchableField[]> => {
  const response = await api.get('flows/searchable_fields/', {
    params: {
      flow_slug: flowSlug,
      company_slug: companySlug,
    },
  })
  return response.data
}

const useFetchSearchableFields = (
  flowSlug: string | null
): UseQueryResult<SearchableField[], AxiosError> => {
  const companySlug = getCompanySlug()
  const queryResult = useQuery<SearchableField[], AxiosError>(
    ['searchable-fields'],
    // eslint-disable-next-line @typescript-eslint/return-await
    async () => fetchSearchableFields(flowSlug, companySlug),
    { onError: (error) => console.error(error) }
  )

  return queryResult
}

const useFlow = (): UseQueryResult<FlowState, AxiosError> => {
  const { pathname } = useLocation()

  const { history } = useSessionState()
  const [dispatch] = useSessionDispatch()
  const [heapDispatch] = useHeapDispatch()

  const flowSlug = useFlowSlug()
  const companySlug = getCompanySlug()

  const [searchParams] = useSearchParams()
  const previewID = searchParams.get('preview_id')
  const [, setSessionFlowIdLS] = useLocalStorage<FlowSessionState['id'] | null>(
    `${flowSlug}/sessionFlowId`,
    null
  )

  const queryResult = useQuery<FlowState, AxiosError>(['flow', companySlug, flowSlug], getFlow, {
    enabled: !pathname.includes(adjustersFlowName),
    onError: (error) => console.error(error),
    onSettled(data) {
      if (data !== undefined && history.length === 0 && previewID === null) {
        const { flow_session: flowSession } = data

        // Store token_subject in Heap if present
        if (data.token_subject) {
          heapDispatch({ type: 'add', fields: { token_subject: data.token_subject } })
        }

        if (has(flowSession, 'flow')) {
          setSessionFlowIdLS(flowSession.id)
          dispatch({
            step: flowSession.current_page,
            type: 'continueFlowStep',
            payload: {
              currentPage: flowSession.current_page ?? data?.start_page.id,
              path: flowSession.path,
              history: flowSession.full_path,
              isSubmitting: false,
            },
          })

          return
        }

        dispatch({ type: 'setStart', step: data.start_page.id })
      }
    },
  })

  return queryResult
}

const extraGtmContainers = (): UseQueryResult<GtmContainer, AxiosError> => {
  const flowSlug = useFlowSlug()
  const companySlug = getCompanySlug()

  return useQuery<GtmContainer, AxiosError>(
    ['gtmExtraContainers', companySlug, flowSlug],
    getExtraGtmContainers
  )
}

type UseStartOverReturn = () => void

function useStartOver(setStartPage = false): UseStartOverReturn {
  const flowSlug = useFlowSlug()
  const { data: flow, refetch: refetchFlow } = useFlow()

  const { reset: resetForm } = useFormContext()
  const [, clearSession] = useSessionDispatch()
  const [, , clearValues] = useLocalStorage(`${flowSlug}/form`, {})

  const [, , clearSessionFlowId] = useLocalStorage(`${flowSlug}/sessionFlowId`, null)
  const [, , clearMetaData] = useLocalStorage(`${flowSlug}/metadata`, {})

  const [, clearHeapState] = useHeapDispatch()

  function startOver(): void {
    const resetValues = getDefaultValues(flow!)
    resetForm(resetValues)
    clearValues()
    clearSession()
    clearHeapState()
    clearSessionFlowId()
    clearMetaData()
    refetchFlow()
      .then(() => {
        if (setStartPage) {
          window.location.reload()
        }
      })
      .catch((error) => {
        console.error(error)
      })
  }

  return startOver
}

function getNames(pages: Page[]): string[] {
  const names = new Set<string>()

  function getElementsNames(elements: Element[]): void {
    elements.forEach((element) => {
      names.add(element.name)
      getElementsNames(element.children)
    })
  }

  getElementsNames(pages.map((page) => page.elements).flat())

  return Array.from(names)
}

function getDefaultValues(flow: FlowState): Record<string, undefined> {
  return getNames(flow.pages).reduce((acc, name) => ({ ...acc, [name]: undefined }), {})
}

export { getDefaultValues, useFlow, useStartOver, extraGtmContainers, useFetchSearchableFields }
